import React, { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { FaBriefcase } from "react-icons/fa";
import "./experience.scss";

gsap.registerPlugin(ScrollTrigger);

const experiences = [
  {
    company: "S & P Global",
    title: "Software Engineer",
    date: "07/2025 – Present | USA",
    bullets: [
      "At S&P Global, I’ve been building scalable, real-time financial systems focused on fraud detection and performance optimization. I developed Spring Boot microservices that reduced false positives by 30% and improved transaction accuracy for millions of users. I optimized SQL queries and introduced Redis caching, reducing API latency by 40% and significantly lowering database load. I also built Python-based ML services for transaction analysis and churn prediction, improving predictive accuracy and enabling proactive retention strategies. Additionally, I led a migration from a monolithic system to AWS serverless architecture, reducing infrastructure costs by $25K/month, and set up CI/CD pipelines using Jenkins and Terraform to improve deployment reliability.",
    ],
  },
  {
    company: "Tata Consultancy Services (TCS)",
    title: "Software Engineer",
    date: "08/2021 – 08/2023 | India",
    bullets: [
      "At TCS, I worked on full-stack development for a cloud-native banking platform using AngularJS and Spring Boot. I built and maintained RESTful microservices on AWS, improving system scalability and reducing latency for high-traffic services. I containerized applications using Docker and implemented CI/CD pipelines with Jenkins, reducing release cycles by 30%. I also optimized PostgreSQL queries to improve API performance and integrated Kafka to enable real-time event-driven processing. Additionally, I built centralized logging and monitoring solutions, reducing production issue resolution time and supported migration of legacy systems to a microservices architecture.",
    ],
  },
  {
    company: "PhonePe",
    title: "Software Developer",
    date: "06/2020 – 07/2021 | India",
    bullets: [
      "At PhonePe, I built backend and frontend features for financial applications, developing RESTful services using Java and JAX-RS to support over 1M API calls per month. I also created dynamic UI components using JavaScript and jQuery to improve user experience and engagement. I refactored legacy C++ modules to fix memory leaks and improve system stability, reducing crashes significantly. Additionally, I integrated third-party KYC APIs to automate onboarding workflows, saving hours of manual effort daily, and implemented RBAC-based security to protect sensitive financial data.",
    ],
  },
];

const Experience = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        ".experience__titleRow",
        { y: 16, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.7,
          ease: "power2.out",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 80%",
          },
        }
      );

      gsap.utils.toArray(".experience__card").forEach((card, i) => {
        gsap.fromTo(
          card,
          { y: 18, opacity: 0, filter: "blur(6px)" },
          {
            y: 0,
            opacity: 1,
            filter: "blur(0px)",
            duration: 0.7,
            ease: "power2.out",
            delay: i * 0.08,
            scrollTrigger: {
              trigger: card,
              start: "top 86%",
            },
          }
        );
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section className="experience" id="Experience" ref={sectionRef}>
      <div className="experience__inner">
        {/* Title + rule */}
        <div className="experience__titleRow">
          <div className="experience__icon">
            <FaBriefcase />
          </div>
          <h2 className="experience__title">Experience</h2>
          <div className="experience__rule" aria-hidden="true" />
        </div>

        {/* Timeline layout */}
        <div className="experience__grid experience__grid--timeline">
          {experiences.map((exp, idx) => {
            // ✅ keep it 3–4 points, shown as a smooth paragraph
            const paragraph = exp.bullets.slice(0, 4).join(" ");

            return (
              <article
                className="experience__card"
                key={`${exp.company}-${idx}`}
              >
                <span className="experience__dot" aria-hidden="true" />

                <div className="experience__cardTop">
                  <div className="experience__companyBlock">
                    <div className="experience__company">{exp.company}</div>
                    <div className="experience__role">{exp.title}</div>
                  </div>
                  <div className="experience__date">{exp.date}</div>
                </div>

                <p className="experience__highlight">{exp.highlight}</p>

                {/* ✅ Removed impacts + removed skills/chips */}
                <p className="experience__summary">{paragraph}</p>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Experience;
